import { BufferAttribute, BufferGeometry, Color, DoubleSide, Vector2 } from 'three';
import type { ShaderMaterial, Texture } from 'three';
import type { MapDef } from '@cloudpearl/config';
import { ATMOSPHERE, LIGHTING, NOISE, SHADOW_FRAG_PARS, SHADOW_VERT_MAIN, SHADOW_VERT_PARS, TRAIL, WORLD_UNIFORMS } from '../shaders/chunks';
import type { SharedUniforms } from '../shaders/shared';
import { col, litMaterial } from './common';

/**
 * Grass after Sucker Punch's Ghost of Tsushima talk:
 *  - blades on a jittered world-space grid, generated entirely in the vertex shader from
 *    gl_InstanceID (no per-blade buffers), in camera-anchored LOD rings — one draw call per ring;
 *  - ground height & island mask come from a toroidal heightfield texture around the camera;
 *  - per-blade height/width/facing/curve from hashes, clumped by a low-frequency patch field;
 *  - curved blades with rounded normals, view-space thickening when seen edge-on;
 *  - wind = scrolling noise for gust strength and direction; the pearl tramples a wake;
 *  - base→tip colour gradient, self-occlusion AO, translucency, specular blended to the terrain
 *    normal with distance (no sparkly aliasing far away).
 */

export interface GrassRing {
  /** Grid spacing (m). */
  spacing: number;
  /** World tile size (m): blades are drawn per visible tile (CPU-culled), never per ring square. */
  tile: number;
  /** Chebyshev half-extents (m): blades live between inner and outer. */
  inner: number;
  outer: number;
  /** Blade segments (curvature detail). */
  segments: number;
}

/** Blades per instance (see the vertex shader). */
export const GRASS_PACK = 8;

const HEIGHTFIELD = /* glsl */ `
uniform sampler2D tHeight;   // RGBA32F toroidal: R height, G mask; 1 texel = 1 m
uniform float uHeightSize;
vec4 hfFetch(vec2 cell) {
  ivec2 c = ivec2(mod(cell, uHeightSize));
  return texelFetch(tHeight, c, 0);
}
// Bilinear height + mask, and the height gradient.
vec4 heightAt(vec2 p, out vec2 grad) {
  vec2 c = floor(p);
  vec2 f = p - c;
  vec4 a = hfFetch(c);
  vec4 b = hfFetch(c + vec2(1.0, 0.0));
  vec4 d = hfFetch(c + vec2(0.0, 1.0));
  vec4 e = hfFetch(c + vec2(1.0, 1.0));
  grad = vec2(mix(b.r - a.r, e.r - d.r, f.y), mix(d.r - a.r, e.r - b.r, f.x));
  return mix(mix(a, b, f.x), mix(d, e, f.x), f.y);
}
// Ground height range (min, max) under the drawn tiles, set by the CPU each frame.
uniform vec2 uGround;
// Cheap cull before any texture fetch: is everything a plant at (x, z) could occupy — ground
// anywhere in uGround, up to its height — outside the view frustum (widened by margin m)?
// Valid for any camera pitch: from straight down (crash shots) to level chase views.
bool offscreen(vec2 p, float top, float m) {
  vec3 a = (viewMatrix * vec4(p.x, uGround.x - 0.3, p.y, 1.0)).xyz;
  vec3 b = (viewMatrix * vec4(p.x, uGround.y + top, p.y, 1.0)).xyz;
  float px = projectionMatrix[0][0];
  float py = projectionMatrix[1][1];
  // Each plane: outside when > 0 for both ends of the span.
  return min(a.z, b.z) > m ||
    min((a.x - m) * px + a.z, (b.x - m) * px + b.z) > 0.0 ||
    min((-a.x - m) * px + a.z, (-b.x - m) * px + b.z) > 0.0 ||
    min((a.y - m) * py + a.z, (b.y - m) * py + b.z) > 0.0 ||
    min((-a.y - m) * py + a.z, (-b.y - m) * py + b.z) > 0.0;
}
`;

const GRASS_VS = /* glsl */ `
${SHADOW_VERT_PARS}
${WORLD_UNIFORMS}
${NOISE}
${HEIGHTFIELD}
${TRAIL}
${ATMOSPHERE}
${LIGHTING}
uniform float uSpacing;
uniform float uTileN;       // blades per tile side
uniform vec4 uTiles[48];    // visible tile origins in cell units, two per vec4
uniform float uInner;
uniform float uOuter;
uniform float uLast;
uniform float uBladeWidth;
uniform vec2 uBladeHeight;
uniform vec3 uGrassBase;
uniform vec3 uGrassTip;
uniform vec3 uGrassDry;
uniform vec3 uGroundA;
uniform vec3 uGroundC;
// Lit colour = vLitA + vLitB * shadow-map term (everything else is resolved per vertex).
varying vec3 vLitA;
varying vec3 vLitB;
void collapse() { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); }
uniform float uBlades;      // blades drawn this frame (visible tiles × blades per tile)
void main() {
  // Instance → (visible tile, blade within tile). Each instance carries GRASS_PACK blades
  // (position.z = which one): a lone 7–13-vertex blade per instance leaves most of a GPU SIMD
  // group idle, packing them roughly halves the cost. Cells are world-anchored, so every blade
  // keeps its identity (position, height, colour) no matter which tiles happen to be drawn.
  float id = float(gl_InstanceID) * ${GRASS_PACK}.0 + position.z;
  if (id >= uBlades) { collapse(); return; }
  float per = uTileN * uTileN;
  float ti = floor(id / per);
  float li = id - ti * per;
  vec4 tv = uTiles[int(ti * 0.5)];
  vec2 tileCell = mod(ti, 2.0) < 0.5 ? tv.xy : tv.zw;
  vec2 cell = tileCell + vec2(mod(li, uTileN), floor(li / uTileN));
  vec2 h2 = hash22(cell * 0.913 + 17.0);
  float r1 = hash12(cell * 1.37 + 3.1);
  float r2 = hash12(cell * 0.71 - 8.3);
  vec2 base2 = (cell + 0.1 + 0.8 * h2) * uSpacing;
  vec2 rel = base2 - cameraPosition.xz;
  float cheb = max(abs(rel.x), abs(rel.y));
  // LOD hand-off: neighbouring rings overlap by a band where one thins out as the other thins in
  // (stochastically, at full height), so coverage stays continuous — no bald stripe.
  const float BAND = 5.0;
  if (cheb < uInner - BAND || cheb > uOuter) { collapse(); return; }
  // Cheap frustum test before any texture fetch (tiles are culled on the CPU; this trims blades).
  if (offscreen(base2, uBladeHeight.y * 2.0, 1.5)) { collapse(); return; }
  float keepIn = uInner > 0.0 ? smoothstep(uInner - BAND, uInner, cheb) : 1.0;
  float keepOut = uLast > 0.5 ? 1.0 : 1.0 - smoothstep(uOuter - BAND, uOuter, cheb);
  if (hash12(cell * 2.17 + 11.0) > keepIn * keepOut) { collapse(); return; }
  vec2 grad;
  vec4 hm = heightAt(base2, grad);
  if (hm.g < 0.6) { collapse(); return; }
  float fadeIn = 1.0;
  // The outermost ring shrinks into the terrain's far-field grass colour instead.
  float fadeOut = uLast > 0.5 ? 1.0 - smoothstep(uOuter * 0.75, uOuter, cheb) : 1.0;
  float mpatch = meadowPatch(base2);
  float clump = 0.75 + 0.5 * vnoise(base2 * 0.35 + 4.0);
  float h = mix(uBladeHeight.x, uBladeHeight.y, r1) * clump * (0.75 + mpatch * 0.5) * fadeIn * fadeOut * min(hm.g * 1.2, 1.0);
  float width = uBladeWidth * (0.7 + r2 * 0.6) * (0.55 + (uSpacing / 0.12) * 0.45);

  float t = position.y;
  float side = position.x;
  float ang = r2 * 6.2831853 + vnoise(base2 * 0.2) * 2.0;
  vec2 face = vec2(cos(ang), sin(ang));
  vec2 perp = vec2(-face.y, face.x);

  // Static curve + lean, then wind (gust strength and direction from scrolling noise).
  float curve = 0.15 + r1 * 0.45;
  vec2 wq = base2 * 0.03 - uWind.xy * uTime * 0.45;
  float gust = vnoise(wq) * 0.7 + vnoise(wq * 2.6 + 9.0) * 0.3;
  vec2 wdir = uWind.xy;
  // Between gusts: a gentle, coherent lean that ripples slowly downwind (no per-blade jitter).
  float lull = sin(dot(base2, uWind.xy) * 0.35 - uTime * 2.2) * 0.5 + 0.5;
  float sway = (0.12 + gust * 0.18 + lull * 0.06) * uWind.z;
  // A pocket of air passing through: blades lean hard downwind with a quick, shared shiver.
  float wave = gustWave(base2);
  sway += wave * (0.85 + 0.1 * sin(uTime * 11.0 - dot(base2, uWind.xy) * 1.4));
  // The pearl's trail: blades it rolled over lie flat along its path (and stay down).
  vec4 tr = trailAt(base2);
  float lay = smoothstep(0.05, 0.6, tr.r);
  vec2 tdir = tr.a > 0.0 ? normalize(tr.gb * 2.0 - 1.0 + vec2(1e-4)) : face;
  vec2 laid = normalize(tdir + vec2(-tdir.y, tdir.x) * (r2 - 0.5) * 0.9) * 0.95;
  vec2 bend = mix(face * curve + wdir * sway, laid, lay);
  // Right under the pearl: pressed down and only nudged aside (no exaggerated parting).
  vec3 toP = vec3(base2.x, hm.r, base2.y) - uPlayerPos;
  float pd = length(toP.xz);
  float press = (1.0 - smoothstep(0.25, 0.8, pd)) * (1.0 - smoothstep(0.4, 1.6, toP.y + 0.6));
  bend += normalize(toP.xz + 1e-4) * press * 0.35;
  float bl = length(bend);
  if (bl > 1.2) bend *= 1.2 / bl;
  float flatten = max(lay * 0.62, press * 0.45);

  float tt = t * t;
  vec3 p = vec3(base2.x, hm.r - 0.03, base2.y);
  float w = width * pow(1.0 - t, 0.8);
  p.xz += perp * side * w + bend * tt * h;
  p.y += t * h * (1.0 - 0.3 * min(dot(bend, bend), 1.5) * tt) * (1.0 - flatten);

  // Rounded normal: the blade face normal tilted across the width (GoT).
  vec3 faceN = normalize(vec3(face.x, 0.0, face.y) - vec3(bend.x, 0.0, bend.y) * t * 0.6 + vec3(0.0, 0.35, 0.0));
  vec3 bladeN = normalize(faceN + vec3(perp.x, 0.0, perp.y) * side * 0.55);
  bladeN = normalize(mix(bladeN, vec3(0.0, 1.0, 0.0), flatten * 0.6));
  vec3 tN = normalize(vec3(-grad.x, 1.0, -grad.y));

  vec4 wPos = vec4(p, 1.0);
  vec4 mv = viewMatrix * wPos;
  // View-space thickening: push edge-on blades open a little so the field stays full.
  vec3 vN = normalize((viewMatrix * vec4(bladeN, 0.0)).xyz);
  float edgeOn = 1.0 - abs(vN.z);
  mv.x += side * w * 0.9 * edgeOn * smoothstep(0.0, 0.3, 1.0 - t);

  // ---- Shading, per vertex. A blade is a few pixels wide, so lighting is Gouraud-interpolated:
  // everything except the shadow-map lookup is folded into colour = A + B * shadow here, and the
  // fragment shader is a single shadow fetch (the bulk of the old per-pixel cost, and far fewer
  // varyings for tile-based GPUs to store).
  vec3 tip = mix(uGroundA * 1.15, uGrassTip, smoothstep(0.24, 0.7, mpatch + (r1 - 0.5) * 0.2));
  tip = mix(tip, uGroundC, smoothstep(0.62, 0.86, mpatch) * 0.35);
  tip = mix(tip, uGrassDry, smoothstep(0.7, 0.9, mpatch + (r1 - 0.5) * 0.25) * 0.7);
  vec3 albedo = mix(uGrassBase, tip, smoothstep(0.0, 1.0, pow(t, 0.8)));
  // Per-blade value/hue jitter: no two neighbours quite the same.
  albedo *= 0.78 + r1 * 0.34;
  albedo = mix(albedo, albedo * vec3(1.06, 1.0, 0.82), step(0.82, fract(r1 * 7.31)) * t);
  // Self-occlusion: denser clumps are darker at the root.
  float ao = mix(0.5 + 0.2 * (1.0 - clump * 0.5), 1.0, smoothstep(0.0, 0.85, t));
  vec3 toCam = cameraPosition - p;
  float dist = length(toCam);
  vec3 V = toCam / max(dist, 1e-4);
  vec3 n = dot(bladeN, V) < 0.0 ? -bladeN : bladeN;
  // Blend toward the terrain normal with distance: soft, stable lighting far away.
  vec3 nl = normalize(mix(n, tN, 0.35 + 0.55 * smoothstep(8.0, 60.0, dist)));
  // paintLight() split into its unshadowed and shadow-scaled parts.
  float wr = clamp((dot(nl, uSunDir) + uWrap) / (1.0 + uWrap), 0.0, 1.0);
  float band = smoothstep(0.0, 0.85, wr);
  vec3 amb = hemiAmbient(nl) * ao;
  vec3 tintHue = uShadowTint / max(dot(uShadowTint, vec3(0.2126, 0.7152, 0.0722)), 1e-3);
  vec3 shadeT = mix(vec3(1.0), tintHue, 0.14);
  vec3 A = albedo * amb * shadeT;
  vec3 B = albedo * (amb * (1.0 - shadeT) + uSunColor) * band;
  // Translucency: sun shining through blades.
  float back = pow(clamp(dot(-V, uSunDir), 0.0, 1.0), 3.0);
  B += tip * uSunColor * back * 0.32 * t;
  // Specular sheen along bent blades (spec normal also blended to terrain with distance).
  vec3 sn = normalize(mix(n, tN, smoothstep(5.0, 40.0, dist)));
  float spec = pow(clamp(dot(normalize(uSunDir + V), sn), 0.0, 1.0), 40.0);
  B += uSunColor * spec * 0.035 * t;
  // Bent over in a gust, blades show their paler, sunlit sides: a soft bright band sweeping by.
  float g = wave * smoothstep(0.2, 0.9, t);
  A *= 1.0 + 0.18 * g;
  B = B * (1.0 + 0.18 * g) + tip * uSunColor * 0.045 * g;
  float contact = mix(contactShade(p), 1.0, t * 0.6);
  // Cloud shadows and drifting mist vary over tens of metres.
  B *= cloudShade(p);
  // Fog (and mist) per vertex: c * (1 - f) + fogColour * f.
  vec3 rd;
  float f = fogAmount(p, rd);
  f = 1.0 - (1.0 - f) * (1.0 - mistAmount(p));
  vLitA = A * contact * (1.0 - f) + fogColorFor(rd) * f;
  vLitB = B * contact * (1.0 - f);

  vec3 wNormal = tN;
  gl_Position = projectionMatrix * mv;
  ${SHADOW_VERT_MAIN}
}
`;

const GRASS_FS = /* glsl */ `
${SHADOW_FRAG_PARS}
varying vec3 vLitA;
varying vec3 vLitB;
void main() {
  gl_FragColor = vec4(vLitA + vLitB * getShadowMask(), 1.0);
}
`;

/**
 * Blade geometry: GRASS_PACK blades of `segments` vertex pairs plus a tip each.
 * x = side (−1..1), y = t (0..1), z = blade slot within the instance.
 */
export function bladeGeometry(segments: number, pack = GRASS_PACK): BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  const per = segments * 2 + 1;
  for (let k = 0; k < pack; k++) {
    const o = k * per;
    for (let i = 0; i < segments; i++) {
      const t = i / segments;
      pos.push(-1, t, k, 1, t, k);
    }
    pos.push(0, 1, k);
    for (let i = 0; i < segments - 1; i++) {
      const a = o + i * 2;
      idx.push(a, a + 1, a + 3, a, a + 3, a + 2);
    }
    const l = o + (segments - 1) * 2;
    idx.push(l, l + 1, o + segments * 2);
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  g.setIndex(idx);
  return g;
}

/** Max visible tiles per ring (two tile origins per vec4 uniform). */
export const GRASS_MAX_TILES = 96;

/** Snap a ring's spacing so a whole number of blades spans each tile. */
export function grassTileLayout(ring: GrassRing): { n: number; spacing: number } {
  const n = Math.max(1, Math.round(ring.tile / ring.spacing));
  return { n, spacing: ring.tile / n };
}

export function createGrassMaterial(shared: SharedUniforms, map: MapDef, heightfield: Texture, ring: GrassRing, last = true): ShaderMaterial {
  const g = map.grass;
  const layout = grassTileLayout(ring);
  return litMaterial(
    shared,
    {
      tHeight: { value: heightfield },
      uHeightSize: { value: (heightfield.image as { width: number }).width },
      uSpacing: { value: layout.spacing },
      uTileN: { value: layout.n },
      uTiles: { value: new Float32Array(GRASS_MAX_TILES * 2) },
      uBlades: { value: 0 },
      uGround: { value: new Vector2(-1e4, 1e4) },
      uInner: { value: ring.inner },
      uOuter: { value: ring.outer },
      uLast: { value: last ? 1 : 0 },
      uBladeWidth: { value: g.width },
      uBladeHeight: { value: [g.height[0], g.height[1]] },
      uGrassBase: col(g.base),
      uGrassTip: col(g.tip),
      uGrassDry: col(g.tipDry),
      uGroundA: col(map.palette.groundA),
      uGroundC: col(map.palette.groundC),
    },
    { name: `grass-${ring.spacing}`, vertexShader: GRASS_VS, fragmentShader: GRASS_FS, side: DoubleSide },
  );
}


// ------------------------------------------------------------------------------------------ flowers

const FLOWER_VS = /* glsl */ `
${SHADOW_VERT_PARS}
${WORLD_UNIFORMS}
${NOISE}
${HEIGHTFIELD}
${TRAIL}
${ATMOSPHERE}
uniform float uSpacing;
uniform float uN;
uniform float uOuter;
uniform float uRatio;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vT;
varying float vHead;
varying float vSpecies;
varying float vRand;
varying vec2 vUv;
void collapse() { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); }
void main() {
  float id = float(gl_InstanceID);
  vec2 cell = floor(cameraPosition.xz / uSpacing) + vec2(mod(id, uN), floor(id / uN)) - floor(uN * 0.5);
  vec2 base2 = (cell + 0.1 + 0.8 * hash22(cell * 1.7 + 5.0)) * uSpacing;
  vec2 rel = base2 - cameraPosition.xz;
  if (max(abs(rel.x), abs(rel.y)) > uOuter) { collapse(); return; }
  if (offscreen(base2, 1.0, 1.0)) { collapse(); return; }
  // Strays everywhere; a light scatter across broad meadow patches; dense clusters within them.
  float r = hash12(cell * 0.37 + 1.0);
  vec2 fq = flowerWarp(base2);
  float region = flowerRegion(fq);
  float clump = flowerClump(fq);
  if (r > uRatio * (0.1 + region * (0.25 + 10.0 * clump * clump))) { collapse(); return; }
  float sp = flowerSpecies(base2);
  if (hash12(cell * 3.1 + 2.0) < 0.2) sp = floor(hash12(cell * 5.7) * 4.999);
  vec2 grad;
  vec4 hm = heightAt(base2, grad);
  if (hm.g < 0.7 || trailAt(base2).r > 0.3) { collapse(); return; }
  float cheb = max(abs(rel.x), abs(rel.y));
  float fade = 1.0 - smoothstep(uOuter * 0.7, uOuter, cheb);
  float r2 = hash12(cell * 2.9 + 8.0);
  // Species: daisy, buttercup, poppy, lupine spike, clover.
  float h;
  vec2 head;
  if (sp < 0.5) { h = 0.18 + r2 * 0.14; head = vec2(0.055, 0.055); }
  else if (sp < 1.5) { h = 0.2 + r2 * 0.16; head = vec2(0.042, 0.042); }
  else if (sp < 2.5) { h = 0.3 + r2 * 0.2; head = vec2(0.075, 0.075); }
  else if (sp < 3.5) { h = 0.42 + r2 * 0.3; head = vec2(0.036, 0.16); }
  else { h = 0.06 + r2 * 0.06; head = vec2(0.038, 0.038); }
  float size = (0.85 + r * 0.4) * fade;
  h *= size;
  head *= size;
  float isHead = step(0.5, uv.y);
  vec3 toCam = cameraPosition - vec3(base2.x, hm.r, base2.y);
  float yaw = atan(toCam.x, toCam.z);
  vec2 face = vec2(sin(yaw), cos(yaw));
  vec2 side = vec2(face.y, -face.x);
  // Wind: a steady lean, gust waves, a little flutter; nudged aside by the pearl.
  vec2 wq = base2 * 0.03 - uWind.xy * uTime * 0.45;
  float wave = gustWave(base2);
  vec2 bend = uWind.xy * (0.1 + vnoise(wq) * 0.2 + wave * 0.8) * uWind.z;
  bend += uWind.xy * sin(uTime * 3.0 - dot(base2, uWind.xy) * 0.9) * 0.025;
  vec3 toP = vec3(base2.x, hm.r, base2.y) - uPlayerPos;
  bend += normalize(toP.xz + 1e-4) * (1.0 - smoothstep(0.35, 1.0, length(toP.xz))) * 0.3;
  float t = position.y;
  vec3 p = vec3(base2.x, hm.r - 0.02, base2.y);
  // Spikes carry their florets along the top of the stem rather than above it.
  float headY = sp > 2.5 && sp < 3.5 ? h - head.y * 0.7 : h;
  p.xz += side * position.x * mix(0.006, head.x * 2.0, isHead) + bend * t * t * h;
  p.y += mix(t * h, headY, isHead) + isHead * position.z * head.y * 2.0;
  vWorldPos = p;
  vNormal = normalize(vec3(face.x * 0.35, 1.0, face.y * 0.35));
  vec3 wNormal = vNormal;
  vec4 wPos = vec4(p, 1.0);
  vT = t;
  vHead = isHead;
  vSpecies = sp;
  vRand = r2;
  vUv = vec2(position.x + 0.5, position.z + 0.5);
  gl_Position = projectionMatrix * viewMatrix * wPos;
  ${SHADOW_VERT_MAIN}
}
`;

const FLOWER_FS = /* glsl */ `
${SHADOW_FRAG_PARS}
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
uniform vec3 uStem;
uniform vec3 uFlowerColors[5];
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vT;
varying float vHead;
varying float vSpecies;
varying float vRand;
varying vec2 vUv;
void main() {
  vec3 albedo = uStem * mix(0.35, 0.75, vT);
  if (vHead > 0.5) {
    vec2 q = vUv * 2.0 - 1.0;
    float r = length(q);
    float a = atan(q.y, q.x) + vRand * 6.2831;
    int sp = int(vSpecies + 0.5);
    vec3 petal = uFlowerColors[0];
    if (sp == 1) petal = uFlowerColors[1];
    else if (sp == 2) petal = uFlowerColors[2];
    else if (sp == 3) petal = uFlowerColors[3];
    else if (sp == 4) petal = uFlowerColors[4];
    if (sp == 0) {
      // Daisy: a ring of slender white petals around a golden eye.
      float edge = 0.6 + 0.4 * pow(abs(cos(a * 6.5)), 0.7);
      if (r > edge) discard;
      albedo = mix(vec3(0.82, 0.58, 0.12), petal, smoothstep(0.22, 0.3, r)) * (0.88 + 0.12 * (1.0 - r));
    } else if (sp == 1) {
      // Buttercup: five rounded glossy petals.
      float edge = 0.7 + 0.3 * cos(a * 5.0);
      if (r > edge) discard;
      albedo = petal * (0.78 + 0.32 * (1.0 - r));
    } else if (sp == 2) {
      // Poppy: four broad, crinkled petals with a dark heart.
      float edge = 0.8 + 0.2 * cos(a * 4.0) + 0.04 * sin(a * 23.0);
      if (r > edge) discard;
      albedo = mix(vec3(0.07, 0.05, 0.06), petal, smoothstep(0.13, 0.22, r)) * (0.85 + 0.2 * (1.0 - r));
    } else if (sp == 3) {
      // Lupine spike: stacked florets tapering to a paler tip.
      float taper = 1.0 - vUv.y * 0.6;
      vec2 fq = vec2(q.x / max(taper, 0.2), fract(vUv.y * 7.0) - 0.5);
      float fl = length(fq * vec2(0.5, 1.05)) + (hash12(floor(vec2(q.x * 3.0, vUv.y * 7.0)) + vRand) - 0.5) * 0.2;
      if (abs(q.x) > taper || fl > 0.5) discard;
      albedo = mix(petal, petal * 1.25 + 0.05, vUv.y) * (0.78 + 0.3 * (1.0 - fl));
    } else {
      // Clover: a soft pink ball of tiny florets.
      float bump = 0.8 + 0.2 * vnoise(q * 9.0 + vRand * 10.0);
      if (r > bump) discard;
      albedo = petal * (0.72 + 0.32 * (1.0 - r));
    }
  }
  float shadow = getShadowMask() * cloudShade(vWorldPos);
  vec3 c = paintLight(albedo, normalize(vNormal), shadow, 1.0);
  // Petals glow softly when the sun is behind them.
  vec3 V = normalize(cameraPosition - vWorldPos);
  c += albedo * uSunColor * pow(clamp(dot(-V, uSunDir), 0.0, 1.0), 4.0) * 0.18 * vHead * shadow;
  gl_FragColor = vec4(applyFog(c, vWorldPos), 1.0);
}
`;

export const FLOWER_RING = { spacing: 0.42, outer: 46 };

export function flowerGeometry(): BufferGeometry {
  const g = new BufferGeometry();
  const pos = [-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0, -0.5, 1, -0.5, 0.5, 1, -0.5, 0.5, 1, 0.5, -0.5, 1, 0.5];
  const uv = [0, 0, 1, 0, 1, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 1];
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('uv', new BufferAttribute(new Float32Array(uv), 2));
  g.setIndex([0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7]);
  return g;
}

export function flowerInstances(): number {
  const n = Math.ceil((FLOWER_RING.outer * 2) / FLOWER_RING.spacing) + 2;
  return n * n;
}

export function createFlowerMaterial(shared: SharedUniforms, map: MapDef, heightfield: Texture): ShaderMaterial {
  const cols = map.grass.flowers.length ? map.grass.flowers : ['#ffffff'];
  // Keep flower albedo below "glow" levels: they're petals, not lamps.
  const colors = [0, 1, 2, 3, 4].map((i) => new Color(cols[i % cols.length]!).multiplyScalar(0.72));
  const n = Math.ceil((FLOWER_RING.outer * 2) / FLOWER_RING.spacing) + 2;
  return litMaterial(
    shared,
    {
      tHeight: { value: heightfield },
      uHeightSize: { value: (heightfield.image as { width: number }).width },
      uSpacing: { value: FLOWER_RING.spacing },
      uN: { value: n },
      uOuter: { value: FLOWER_RING.outer },
      uRatio: { value: map.grass.flowerRatio },
      uGround: { value: new Vector2(-1e4, 1e4) },
      uStem: col(map.grass.tip),
      uFlowerColors: { value: colors },
    },
    { name: 'flowers', vertexShader: FLOWER_VS, fragmentShader: FLOWER_FS, side: DoubleSide },
  );
}
